/**
 * Email validation utility functions
 */

/**
 * Email validation result interface
 */
export interface EmailValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Common email patterns
 */
export const EMAIL_PATTERNS = {
  // Standard email pattern (RFC 5322 compliant, simplified)
  STANDARD:
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
  // Strict email pattern (more restrictive)
  STRICT:
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
} as const;

/**
 * Validates email address format
 * @param email - Email address to validate
 * @param strict - Use strict validation pattern (default: true)
 * @returns EmailValidationResult
 */
export function validateEmail(
  email: string,
  strict: boolean = true
): EmailValidationResult {
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      errorMessage: 'common.validation.email_required',
    };
  }

  const trimmedEmail = email.trim();

  if (trimmedEmail === '') {
    return {
      isValid: false,
      errorMessage: 'common.validation.email_required',
    };
  }

  // Check email length (max 254 characters per RFC 5321)
  if (trimmedEmail.length > 254) {
    return {
      isValid: false,
      errorMessage: 'common.validation.email_too_long',
    };
  }

  // Validate email format
  const pattern = strict ? EMAIL_PATTERNS.STRICT : EMAIL_PATTERNS.STANDARD;
  const isValid = pattern.test(trimmedEmail);

  if (!isValid) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_email_format',
    };
  }

  // Additional checks
  const parts = trimmedEmail.split('@');
  if (parts.length !== 2) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_email_format',
    };
  }

  const [localPart, domain] = parts;

  // Local part validation (before @)
  if (localPart.length === 0 || localPart.length > 64) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_email_format',
    };
  }

  // Domain validation (after @)
  if (domain.length === 0 || domain.length > 253) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_email_format',
    };
  }

  // Check for consecutive dots
  if (localPart.includes('..') || domain.includes('..')) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_email_format',
    };
  }

  // Check domain has at least one dot
  if (!domain.includes('.')) {
    return {
      isValid: false,
      errorMessage: 'common.validation.invalid_email_format',
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Validates email address for Angular reactive forms
 * @param control - Angular form control
 * @returns Validation error object or null
 * @example
 * // In component:
 * emailValidator = emailValidatorFn;
 *
 * // In form:
 * email: ['', [Validators.required, this.emailValidator]]
 */
export function emailValidatorFn(control: any) {
  if (!control.value) {
    return null; // Let required validator handle empty values
  }

  const validation = validateEmail(control.value, true);

  if (!validation.isValid) {
    return {
      invalidEmail: {
        message:
          validation.errorMessage ||
          'common.validation.invalid_email_format',
      },
    };
  }

  return null;
}

